import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalManualLocationOrderContext } from "./GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "./UserSettingsContext";

const DEBOUNCE_MS = 500;

export const GlobalManualLocationOrderProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { settings, updateSettings } = useUserSettingsContext();
	const serverState = settings.customLocationOrder ?? { enabled: false, orderedLocationNames: [] };

	const [enabled, setEnabledLocal] = useState(serverState.enabled);
	const [orderedLocationNames, setOrderedLocal] = useState(serverState.orderedLocationNames);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

	useEffect(() => {
		setEnabledLocal(serverState.enabled);
		setOrderedLocal(serverState.orderedLocationNames);
	}, [serverState]);

	const persistToServer = useCallback(
		(newEnabled: boolean, newNames: string[]) => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(async () => {
				await updateSettings({ customLocationOrder: { enabled: newEnabled, orderedLocationNames: newNames } });
			}, DEBOUNCE_MS);
		},
		[updateSettings],
	);

	const cancelPendingUpdate = useCallback(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
	}, []);

	useEffect(
		// Clean up debounce on unmount
		() => () => cancelPendingUpdate(),
		[cancelPendingUpdate],
	);

	const setEnabled = useCallback(
		(newEnabled: boolean) => {
			setEnabledLocal(newEnabled);
			persistToServer(newEnabled, orderedLocationNames);
		},
		[persistToServer, orderedLocationNames],
	);

	const setOrderedLocationNames = useCallback(
		(newNames: string[]) => {
			setOrderedLocal(newNames);
			persistToServer(enabled, newNames);
		},
		[persistToServer, enabled],
	);

	const customStringOrderMap = useMemo(
		() => (enabled && orderedLocationNames.length > 0 ? new Map(orderedLocationNames.map((name, idx) => [name, idx])) : null),
		[enabled, orderedLocationNames],
	);

	return (
		<GlobalManualLocationOrderContext.Provider
			value={{
				enabled,
				setEnabled,
				orderedLocationNames,
				setOrderedLocationNames,
				customStringOrderMap,
				cancelPendingUpdate,
			}}
		>
			{children}
		</GlobalManualLocationOrderContext.Provider>
	);
};
